import React, { useEffect, useState } from "react";
import styles from "./FieldHistory.module.scss";
import { useTheme, useSingleton } from "../../hooks/useSingleton";
import { FieldInstance, FieldTemplate, Option } from "db/definitions";
import { getRevisionsByFieldInstance } from "db/queries/revisionQueries";
import { Revision } from "db/definitions";
import { FiCornerUpRight, FiX, FiCopy } from "react-icons/fi";
import { Button } from "@factorearth/component-library";

interface Props {
	currentFieldInstance: FieldInstance;
	fieldTemplate: FieldTemplate;
	handleRevert: (value: string) => void;
	menuToggleHandler: () => void;
	online: boolean;
	handleToggleHistory: () => void;
}

interface ShortValueObj {
	short: string;
	long: string;
}

function FieldHistory(props: Props) {
	const { currentFieldInstance, fieldTemplate, handleRevert, menuToggleHandler, online } = props;
	// value delimiter mainly for picklist types
	const delim = String.fromCharCode(31);
	// the maximum allowed number of characters to display of the value for the revision.
	const maxCharacters = 20;
	const [historyData, setHistoryData] = useState<React.JSX.Element[]>([]);
	const color = useTheme();
	const { mode } = useSingleton();
	const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
	const [revisions, setRevisions] = useState<Revision[]>([]);
	const [loaded, setLoaded] = useState(false);

	useEffect(() => {
		if (currentFieldInstance && currentFieldInstance.id) {
			async function setupRevisions() {
				const retrievedRevisions = await getRevisionsByFieldInstance(currentFieldInstance.id, online);
				setRevisions(retrievedRevisions);
				setLoaded(true);
			}
			setupRevisions();
		}
	}, []);

	useEffect(() => {
		const newHistory = handleHistoryRenderizer();
		setHistoryData(newHistory);
	}, [fieldTemplate, currentFieldInstance, expandedIndex, revisions]);

	function handleRevertClick(data: string): void {
		handleRevert(data);
		props.handleToggleHistory();
	}

	function copyValue(value: string): void {
		navigator.clipboard.writeText(value);
	}

	/**
	 * Renderization!!!
	 * This will manage the set up of the html for the field history system for the DOM
	 * It will display everything as a string. No need to show interactive checkboxes and whatnot to take up space.
	 * This is counting on the author and date being the last two things in the delim separated string value.
	 * (delim var)
	 */
	function handleHistoryRenderizer(): React.JSX.Element[] {
		let finalCards = [];
		revisions.sort((revA, revB) => {
			const dateA = new Date(revA.createdAt);
			const dateB = new Date(revB.createdAt);
			return dateB.getTime() - dateA.getTime();
		});
		for (let i = 0; i < revisions.length; i++) {
			const revisionData = revisions[i];

			// const matchingObject = fieldTemplate.options.items.find((obj) => obj.id === splitData[0]);
			const date = new Date(revisionData.createdAt);
			const author = revisionData.author;
			const readableDate = date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" });
			const valueString = revisionData.value;
			const valueStringProxyObj = setupValueStringProxy([valueString]);
			const html = (
				<div
					style={{ background: color.background.primary, borderRadius: mode ? "none" : "7px" }}
					key={revisionData.id}
					id="historyMenu"
					className={styles.fieldHistoryCard}>
					<div className={styles.historyCardContent}>
						<div
							className={styles.fieldHistory_user}
							style={{
								color: color.text.primary,
							}}>
							{/* circle icon */}
							<div className={styles.circle} />
							{/* author */}
							{author || "No Author Found"}
						</div>
						<div
							className={styles.fieldHistory_date}
							style={{
								color: color.text.primary,
							}}>
							{readableDate || "No Date Found"}
						</div>
						<div className={styles.value_proxy_wrapper} style={{ color: color.text.primary }}>
							{valueStringProxyObj.long}
						</div>
					</div>
					<div className={styles.historyCardBtns}>
						<Button
							colorPalette={color}
							className={styles.historyCardBtn}
							style={{ color: color.background.primary }}
							onClick={() => handleRevertClick(valueString)}
							variant="filled"
							icon={<FiCornerUpRight />}
						/>
						<Button
							colorPalette={color}
							style={{ color: color.background.primary }}
							className={styles.historyCardBtn}
							variant="filled"
							onClick={() => copyValue(valueString)}
							icon={<FiCopy />}
						/>
					</div>
				</div>
			);
			finalCards.push(html);
		}

		return finalCards;
	}

	/**
	 * setup the string that is displayed for the user for each revision item.
	 */
	function setupValueStringProxy(stringArray: string[]): ShortValueObj {
		let final = { short: "", long: "" };
		switch (fieldTemplate.inputType) {
			case "phone":
			case "text":
			case "number":
			case "note":
				final = parseTextType(stringArray);
				break;
			case "radio":
				final = parseRadio(stringArray);
				break;
			case "dropdown":
			case "checkbox":
				final = parseCheckboxAndDropdown(stringArray[0].split(delim));
				break;
			case "date":
				final = parseDate(stringArray);
				break;
			default:
				break;
		}
		return final;
	}
	/**
	 * Parses revision element into short and long readable forms for text type
	 * @param stringArray of the revision history elements split up by delim
	 * @returns object with short and long versions.
	 */
	function parseTextType(stringArray: string[]): ShortValueObj {
		const string = stringArray[0];
		const newString = string.length > maxCharacters ? string.slice(0, maxCharacters) + "..." : string;

		return { short: newString, long: string };
	}
	/**
	 * Parses revision element into short and long readable forms for radio type
	 * @param stringArray of the revision history elements split up by delim
	 * @returns object with short and long versions.
	 */
	function parseRadio(stringArray: string[]): ShortValueObj {
		const string = stringArray[0];
		let option = string;
		if (fieldTemplate.options) {
			for (let i = 0; i < fieldTemplate.options.items.length; i++) {
				const optionInList = fieldTemplate.options.items[i];
				if (string === optionInList.id) {
					option = optionInList.label;
				}
			}
		}
		const short = option.length > maxCharacters ? option.slice(0, maxCharacters) + "..." : option;
		return { long: option, short };
	}
	/**
	 * Parses revision element into short and long readable forms for checkbox and dropdown type
	 * @param stringArray of the revision history elements split up by delim
	 * @returns object with short and long versions.
	 */
	function parseCheckboxAndDropdown(stringArray: string[]): ShortValueObj {
		if (fieldTemplate.options) {
			let finalStringArray = [];
			let optionMap: { [key: string]: Option } = {};
			for (let i = 0; i < fieldTemplate.options.items.length; i++) {
				const option = fieldTemplate.options.items[i];
				optionMap[option.id] = option;
			}
			for (let i = 0; i < stringArray.length; i++) {
				const stringArrayElement = stringArray[i];
				if (optionMap[stringArrayElement]) {
					finalStringArray.push(optionMap[stringArrayElement].label);
				} else {
					finalStringArray.push(stringArrayElement);
				}
			}

			// doing comma because this is for proxy and is more readable than the charcode.
			const long = finalStringArray.join(",");
			const short = long.length > maxCharacters ? long.slice(0, maxCharacters) + "..." : long;
			return { short, long };
		}
		return { short: "", long: "" };
	}

	/**
	 * Parses revision element into short and long readable forms for date type
	 * @param stringArray of the revision history elements split up by delim
	 * @returns object with short and long versions.
	 */
	function parseDate(stringArray: string[]): ShortValueObj {
		const date = new Date(`${stringArray[0]}T00:00:00.000Z`);
		const readableDate = date.toUTCString();
		const short = readableDate.length > maxCharacters ? readableDate.slice(0, maxCharacters) + "..." : readableDate;
		return { long: readableDate.replace("00:00:00 GMT", ""), short };
	}

	return (
		<div className={styles.fieldHistoryWrapper}>
			<div className={styles.fieldHistoryDrawer} style={{ background: color.background.primary }}>
				<div
					style={{
						color: color.text.primary,
						background: color.background.primary,
					}}
					className={styles.historyHeader}>
					<div className={styles.headerText}>
						Field History
						<div className={styles.close_btn} onClick={() => menuToggleHandler()}>
							<FiX />
						</div>
					</div>
					<div className={styles.headerData}>Field Type: {fieldTemplate.inputType.charAt(0).toUpperCase() + fieldTemplate.inputType.slice(1)}</div>
				</div>
				{loaded ? (
					revisions.length === 0 ? (
						<>
							<div style={{ color: color.text.primary, background: color.background.tertiary }} className={styles.noHistory}>
								No History
							</div>
							<div className={styles.headerData}>
								Field Type: {fieldTemplate.inputType.charAt(0).toUpperCase() + fieldTemplate.inputType.slice(1)}
							</div>
						</>
					) : (
						<div style={{ background: color.background.tertiary }} className={styles.historyData}>
							{historyData}
							<div className={styles.done_btn} style={{ color: color.text.tertiary }} onClick={() => menuToggleHandler()}>
								Done
							</div>
						</div>
					)
				) : (
					<div style={{ color: color.text.primary, background: color.background.tertiary }} className={styles.noHistory}>
						Loading...
					</div>
				)}
			</div>
		</div>
	);
}

export default FieldHistory;
